import React, { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { IoArrowBackCircle } from "react-icons/io5";
import FullPageLoading from "../../components/FullPageLoading/FullPageLoading";
import useTenantDetails from "../../customHooks/TenantDetails/useTenantDetails";
import {
  FaCheckCircle,
  FaLock,
  FaToggleOff,
  FaToggleOn,
  FaChevronRight,
} from "react-icons/fa";
import { AiOutlineCopy } from "react-icons/ai";
import { Button } from "@mui/material";
import toast from "react-hot-toast";
import { Modal } from "react-bootstrap";
import { Loader as LoaderIcon } from "react-feather";
import "./TenantDetails.css";
import { TiTick } from "react-icons/ti";
import { FaXmark } from "react-icons/fa6";
import { ImCheckmark } from "react-icons/im";

const TenantDetails = () => {
  const { id } = useParams();
  const {
    loader,
    tenantData,
    activeTab,
    setActiveTab,
    formData,
    setFormData,
    handleFormUpdate,
    updateTenantDetails,
    handleToggleActive,
    handleLogoUpload,
    plans,
    isStandard,
    setIsStandard,
    customLoader,
    handleOpenModal,
    handleCloseModal,
    isModalOpen,
    errors,
    setErrors,
    handleIsStandardChange,
    handlePlanSelection,
    handleSubscriptionTypeSelection,
    submitLoader,
    handleTabChange,
    storeUsage,
    handleAddDomain,
    whiteListedDomains,
    handleRemoveDomain,
    handleInputChange,
    modulePermissions,
    selected,
    handleCheckboxChange,
    ALL_AVAILABLE_SERVICES,
    selectedServices,
    handleServiceToggle,
    handleStoreKeyChange,
    handleSupportFieldChange,
    handleTenantStatusToggle,
    allDomainOptions,
    onboardingDefaultKeys,
    // --- STAGE PROPS ---
    selectedEnvId,
    stageInputData,
    isPreparingNextStage,
    handleStageTabClick,
    handleStageFieldChange,
    handleStageChecklistToggle,
    updateStageInfo,
    handlePrepareNextStage,
    activateNextStage,
    getCurrentEnvIndex,
  } = useTenantDetails({ id });

  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const STORE_DEFAULT_KEY_NAME_MAP = {
    webhook: "np_communication_webhook",
    settlement_agency_public_key: "settlement_agency_public_key",
    np_subscriber_private_id: "subscriber_private_id",
    np_unique_id: "unique_id",
    np_signing_public_id: "signing_public_id",
  };

  const SETTLEMENT_CONFIG_KEYS = new Set([
    "settlement_agency_public_key",
    "np_subscriber_private_id",
    "np_unique_id",
    "np_signing_public_id",
  ]);

  const CONFIG_KEYS = [
    { sKey: "webhook", label: "Webhook" },
    { sKey: "np_unique_id", label: "Unique Id" },
    { sKey: "np_subscriber_private_id", label: "Subscriber Private Id" },
    { sKey: "np_signing_public_id", label: "Signing Public Id" },
    {
      sKey: "settlement_agency_public_key",
      label: "Settlement Agency Public Key",
    },
  ];

  const mergedConfigKeys = CONFIG_KEYS.map((cfg) => {
    const existing = tenantData?.storeDetails[0]?.keys.find(
      (k) => k.sKey === cfg.sKey,
    );

    return {
      ...cfg,
      sValue: existing ? existing.sValue : "",
      prefilled: !!existing,
    };
  });

  const shouldShowMsnCommission = tenantData?.activeServices?.includes("MSN");

  const renderStageTabs = () => {
    if (!tenantData?.environmentInfo) return null;

    const currentIndex = getCurrentEnvIndex(tenantData.environmentInfo);

    return (
      <div className="flex items-center mb-4 overflow-x-auto pb-2 border-b border-gray-200">
        {tenantData.environmentInfo.map((env, index) => {
          const isCurrentActiveStage = env.isCurrentEnvironment;
          const isPast = index < currentIndex;
          const isFuture = index > currentIndex;
          const isSelected = selectedEnvId === env.environment_id;

          // Compact Styles
          let containerClass =
            "relative flex items-center group cursor-pointer select-none";
          let circleClass =
            "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border transition-colors duration-200 z-10 ";
          let textClass = "ml-2 text-xs font-medium whitespace-nowrap ";

          if (isCurrentActiveStage) {
            circleClass +=
              "bg-green-600 border-green-600 text-white shadow-sm ring-1 ring-green-200";
            textClass += "text-green-700 font-bold";
          } else if (isPast) {
            circleClass += "bg-blue-600 border-blue-600 text-white";
            textClass += "text-blue-700";
          } else if (isSelected) {
            circleClass += "bg-white border-blue-500 text-blue-500";
            textClass += "text-gray-800";
          } else {
            containerClass =
              "relative flex items-center cursor-not-allowed opacity-60";
            circleClass += "bg-gray-100 border-gray-300 text-gray-400";
            textClass += "text-gray-400";
          }

          if (isSelected && !isCurrentActiveStage) {
            circleClass += " ring-1 ring-blue-300";
          }

          return (
            <React.Fragment key={env.environment_id}>
              <div
                className={containerClass}
                onClick={() =>
                  !isFuture && handleStageTabClick(env.environment_id)
                }
              >
                <div className={circleClass}>
                  {isPast ? <TiTick size={14} /> : index + 1}
                </div>
                <div className="flex flex-col">
                  <span className={textClass}>
                    {env.environment_name.toUpperCase()}
                  </span>
                </div>
              </div>

              {index < tenantData.environmentInfo.length - 1 && (
                <div className="flex items-center mx-3 text-gray-300">
                  <FaChevronRight size={10} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const onConfirmNextStage = () => {
    setShowConfirmModal(false);
    handlePrepareNextStage(); // Just prepares UI, no API call
  };

  return (
    <div className="container mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Tenant Details</h1>
        <button
          onClick={() => navigate("/merchants")}
          className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
        >
          <IoArrowBackCircle size={20} /> Back
        </button>
      </div>

      {loader && <FullPageLoading />}

      {!loader && tenantData ? (
        <div className="bg-white p-6 mt-6 shadow-sm rounded-lg border">
          <div className="flex items-center justify-between mb-4 bg-gray-50 p-3 rounded border">
            <div className="flex items-center gap-3">
              <img
                src={tenantData.brandLogo}
                alt="Brand Logo"
                className="w-12 h-12 rounded-full border bg-white"
              />

              <div>
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  {tenantData.brandName}
                  <button
                    className="text-gray-400 hover:text-gray-800 transition"
                    title="Copy Store URL"
                    onClick={() => {
                      const url = tenantData?.storeDetails[0]?.keys.find(
                        (url) => url.sKey === "store_url",
                      )?.sValue;
                      navigator.clipboard.writeText(url);
                      toast.success("Store URL copied!");
                    }}
                  >
                    <AiOutlineCopy className="w-4 h-4" />
                  </button>
                </h2>

                <div className="text-gray-600 text-xs flex gap-4 mt-1">
                  <span>
                    Joined: {new Date(tenantData.joinedAt).toLocaleDateString()}
                  </span>
                  <span>
                    Stage: <b>{tenantData.environment_name}</b>
                  </span>
                  <span>
                    Np: <b>{tenantData.npType}</b>
                  </span>
                  <span>
                    Domain: <b>{tenantData.domain}</b>
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span
                className={`text-xs font-bold uppercase ${
                  tenantData.sStatus === "active"
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {tenantData.sStatus}
              </span>
              <button
                onClick={handleTenantStatusToggle}
                className="transition-transform active:scale-90"
              >
                {tenantData.sStatus === "active" ? (
                  <FaToggleOn size={30} className="text-green-500" />
                ) : (
                  <FaToggleOff size={30} className="text-red-500" />
                )}
              </button>
            </div>
          </div>

          <div className="border-b mb-3">
            <div className="flex space-x-4">
              {[
                "userinfo",
                "storeDetails",
                "activeservices",
                "tenantStages",
              ].map((tab) => (
                <button
                  key={tab}
                  className={`py-2 px-2 border-b-2 font-medium text-sm focus:outline-none transition-colors ${
                    activeTab === tab || formData.sType === tab
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab === "userinfo" && "Tenant Information"}
                  {tab === "storeDetails" && "Store Details"}
                  {tab === "activeservices" && "Services & Support"}
                  {tab === "tenantStages" && "Tenant Stages"}
                </button>
              ))}
            </div>
          </div>

          <div>
            {formData.sType === "userinfo" && (
              <div>
                <div className="mt-2 flex items-center gap-4">
                  <label className="text-gray-700 font-medium">Email:</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleFormUpdate("email", e.target.value)}
                    className="border p-2 rounded-md w-80"
                  />
                  <button
                    onClick={updateTenantDetails}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </div>

                <div className="my-6">
                  <h3 className="text-lg font-semibold text-gray-800">Tenants</h3>
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Created At</th>
                          <th>Updated At</th>
                          <th>Consent for Notification</th>
                          <th>Status</th>
                          <th>Role</th>
                          {shouldShowMsnCommission && <th>MSN Commission</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {tenantData.users.map((user, index) => (
                          <tr key={index}>
                            <td>{user.sName}</td>
                            <td>{user.sEmail}</td>
                            <td>
                              {new Intl.DateTimeFormat("en-US", {
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                              }).format(new Date(user.dtCreatedAt))}
                            </td>
                            <td>
                              {new Intl.DateTimeFormat("en-US", {
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                              }).format(new Date(user.dtUpdatedAt))}
                            </td>
                            <td>
                              {user.bSubscribedNotification ? "Yes" : "No"}
                            </td>
                            <td className="py-2 px-4">
                              {tenantData.sStatus === "active" ? (
                                <span className="text-green-600 font-semibold">
                                  Active
                                </span>
                              ) : (
                                <span className="text-red-600 font-semibold">
                                  Inactive
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-4 text-gray-800">
                              {user.sRole}
                            </td>
                            {shouldShowMsnCommission && (
                              <td className="py-2 px-4">
                                {tenantData.msnCommission ? (
                                  <div className="flex w-full min-h-[24px] items-stretch">
                                    <div className="flex flex-1 items-center justify-end pr-4 text-gray-700 whitespace-nowrap">
                                      {tenantData.msnCommission.type
                                        ?.charAt(0)
                                        .toUpperCase() +
                                        tenantData.msnCommission.type?.slice(
                                          1
                                        )}
                                    </div>
                                    <span
                                      className="self-stretch w-px bg-gray-300"
                                      aria-hidden="true"
                                    />
                                    <div className="flex flex-1 items-center pl-4 text-gray-700 font-semibold whitespace-nowrap">
                                      {tenantData.msnCommission.amount}
                                      {tenantData.msnCommission.valueType ===
                                      "percent"
                                        ? "%"
                                        : "₹"}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-gray-500">-</span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div>
            {formData.sType === "storeDetails" && (
              <div className="p-4 space-y-4">
                <div className="flex flex-row gap-4">
                  <label className="text-gray-700 font-medium">
                    Brand Logo:
                  </label>
                  <div className="flex items-center gap-4">
                    {/* Display the current brand logo */}
                    <img
                      src={formData.brandLogo}
                      alt="Brand Logo"
                      className="w-32 h-32 object-cover border rounded-md"
                    />

                    <label
                      htmlFor="brandLogoUpload"
                      className="flex items-center gap-2 cursor-pointer bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M16.5 12.75V19.5a2.25 2.25 0 01-2.25 2.25h-4.5A2.25 2.25 0 017.5 19.5v-6.75m4.5 6.75V4.5m-3 3l3-3 3 3"
                        />
                      </svg>
                      <span>Update Logo</span>
                    </label>
                    <input
                      id="brandLogoUpload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-3 mt-6">
                  <label className="text-gray-700 font-bold border-b pb-2">
                    Retail Domains Permissions:
                  </label>
                  <p className="text-sm text-gray-500">
                    Select the domains this tenant is allowed to operate in:
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 bg-gray-50 p-4 rounded-lg border">
                    {allDomainOptions.map((option) => {
                      const isChecked = formData.retailDomains?.includes(
                        option.value,
                      );
                      return (
                        <label
                          key={option.value}
                          className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-all ${
                            isChecked
                              ? "bg-blue-50 border-blue-400 shadow-sm"
                              : "bg-white border-gray-200 hover:border-blue-300"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="h-5 w-5 rounded text-blue-600 focus:ring-blue-500"
                            checked={isChecked}
                            onChange={(e) => {
                              const currentDomains =
                                formData.retailDomains || [];
                              const updatedDomains = e.target.checked
                                ? [...currentDomains, option.value]
                                : currentDomains.filter(
                                    (val) => val !== option.value,
                                  );

                              handleFormUpdate("retailDomains", updatedDomains);
                            }}
                          />
                          <span
                            className={`text-sm font-medium ${
                              isChecked ? "text-blue-800" : "text-gray-700"
                            }`}
                          >
                            {option.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-gray-700 font-medium">
                    Store Config Keys:
                  </label>
                  <ul className="space-y-2">
                    {CONFIG_KEYS.map((key) => {
                      const existing = tenantData?.storeDetails[0]?.keys.find(
                        (k) => k.sKey === key.sKey && !!k.sValue,
                      );

                      const onboardingDefaultValue =
                        onboardingDefaultKeys?.[
                          STORE_DEFAULT_KEY_NAME_MAP[key.sKey]
                        ] || "";

                      const isPrefilled = !!existing || !!onboardingDefaultValue;

                      const valueInForm =
                        formData.storeConfigKeys.find(
                          (item) => item.sKey === key.sKey,
                        )?.sValue ||
                        existing?.sValue ||
                        onboardingDefaultValue ||
                        "";

                      const isSettlementConfigField =
                        SETTLEMENT_CONFIG_KEYS.has(key.sKey);
                      const isDisabled =
                        (isPrefilled && key.sKey !== "webhook") ||
                        (isSettlementConfigField && !!onboardingDefaultValue);

                      return (
                        <li key={key.sKey}>
                          <label className="text-gray-700">{key.label}</label>
                          <input
                            type="text"
                            value={valueInForm}
                            onChange={(e) =>
                              setFormData((prevFormData) => {
                                const exists =
                                  prevFormData.storeConfigKeys.some(
                                    (item) => item.sKey === key.sKey,
                                  );
                                return {
                                  ...prevFormData,
                                  storeConfigKeys: exists
                                    ? prevFormData.storeConfigKeys.map((item) =>
                                        item.sKey === key.sKey
                                          ? { ...item, sValue: e.target.value }
                                          : item,
                                      )
                                    : [
                                        ...prevFormData.storeConfigKeys,
                                        {
                                          sKey: key.sKey,
                                          sValue: e.target.value,
                                        },
                                      ],
                                };
                              })
                            }
                            disabled={isDisabled}
                            className={`border p-2 rounded-md w-full ${
                              isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                            }`}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={updateTenantDetails}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            )}
          </div>
          <div>
            {activeTab === "activeservices" && (
              <div className="space-y-6">
                {/* Active Services Selection */}
                <div>
                  <h3 className="section-title">Active Services</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Select the services that should be active for this tenant.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {ALL_AVAILABLE_SERVICES.filter((serviceName) => {
                      // Hide ISN if MSN is selected
                      if (serviceName === "ISN" && selectedServices.includes("MSN")) {
                        return false;
                      }
                      // Hide MSN if ISN is selected
                      if (serviceName === "MSN" && selectedServices.includes("ISN")) {
                        return false;
                      }
                      return true;
                    }).map((serviceName) => {
                      const isMSNProvider = tenantData?.domain === "Retail - MSN Provider";
                      const mandatoryServices = ["IGM", "RSF", "MSN"];
                      const isMandatoryService = isMSNProvider && mandatoryServices.includes(serviceName);
                      const isSelected = selectedServices.includes(serviceName);
                      const cannotUncheck = isMandatoryService && isSelected;

                      return (
                        <label
                          key={serviceName}
                          className={`flex items-center justify-between p-3 border rounded-lg transition-all duration-150 ${
                            cannotUncheck ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'
                          } ${
                            isSelected
                              ? "bg-blue-50 border-blue-400 ring-1 ring-blue-500 shadow-sm"
                              : "bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                          }`}
                          title={cannotUncheck ? "This service is mandatory for MSN Providers" : ""}
                        >
                          <span
                            className={`text-sm font-medium ${
                              isSelected
                                ? "text-blue-700"
                                : "text-gray-700"
                            }`}
                          >
                            {serviceName}
                            {cannotUncheck && <span className="text-red-600 ml-1">*</span>}
                          </span>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              if (cannotUncheck) return;
                              handleServiceToggle(serviceName);
                            }}
                            disabled={cannotUncheck}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-offset-0"
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Customer Support Section */}
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="section-title">Customer Support Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label
                        htmlFor="cs_name"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Name
                      </label>
                      <input
                        id="cs_name"
                        type="text"
                        value={formData.customer_support.name || ""}
                        onChange={(e) =>
                          handleSupportFieldChange(
                            "customer_support",
                            "name",
                            e.target.value,
                          )
                        }
                        className="input-field"
                        placeholder="Enter name"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="cs_email"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Email
                      </label>
                      <input
                        id="cs_email"
                        type="email"
                        value={formData.customer_support.email || ""}
                        onChange={(e) =>
                          handleSupportFieldChange(
                            "customer_support",
                            "email",
                            e.target.value,
                          )
                        }
                        className="input-field"
                        placeholder="Enter email"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="cs_phone"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Phone
                      </label>
                      <input
                        id="cs_phone"
                        type="tel"
                        value={formData.customer_support.phone || ""}
                        onChange={(e) =>
                          handleSupportFieldChange(
                            "customer_support",
                            "phone",
                            e.target.value,
                          )
                        }
                        className="input-field"
                        placeholder="Enter phone"
                        maxLength="10" // Optional: Add maxLength
                      />
                    </div>
                  </div>
                </div>

                {/* GRO Support Section */}
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="section-title">GRO Support Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label
                        htmlFor="gro_name"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Name
                      </label>
                      <input
                        id="gro_name"
                        type="text"
                        value={formData.gro_support.name || ""}
                        onChange={(e) =>
                          handleSupportFieldChange(
                            "gro_support",
                            "name",
                            e.target.value,
                          )
                        }
                        className="input-field"
                        placeholder="Enter name"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="gro_email"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Email
                      </label>
                      <input
                        id="gro_email"
                        type="email"
                        value={formData.gro_support.email || ""}
                        onChange={(e) =>
                          handleSupportFieldChange(
                            "gro_support",
                            "email",
                            e.target.value,
                          )
                        }
                        className="input-field"
                        placeholder="Enter email"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="gro_phone"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Phone
                      </label>
                      <input
                        id="gro_phone"
                        type="tel"
                        value={formData.gro_support.phone || ""}
                        onChange={(e) =>
                          handleSupportFieldChange(
                            "gro_support",
                            "phone",
                            e.target.value,
                          )
                        }
                        className="input-field"
                        placeholder="Enter phone"
                        maxLength="10" // Optional: Add maxLength
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <button
                    onClick={() => updateTenantDetails()}
                    className="save-button"
                    disabled={submitLoader}
                  >
                    {submitLoader ? (
                      <LoaderIcon size={18} className="indicator" />
                    ) : (
                      "Save Services & Support"
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* --- TENANT STAGES CONTENT (Compact) --- */}
          <div>
            {formData.sType === "tenantStages" &&
              tenantData?.environmentInfo && (
                <div className="py-2">
                  {/* 1. Environment Stepper */}
                  {renderStageTabs()}

                  {/* 2. Content Area */}
                  <div className="bg-white">
                    {(() => {
                      const currentEnvObj = tenantData.environmentInfo.find(
                        (e) => e.isCurrentEnvironment,
                      );

                      const isLastStage =
                        tenantData.environmentInfo.findIndex(
                          (e) => e.environment_id === selectedEnvId,
                        ) ===
                        tenantData.environmentInfo.length - 1;

                      // LOGIC: View is Editable if:
                      // 1. It is the Current Active Stage
                      // 2. OR we are "Preparing" the next stage (UI switched)
                      const isEditableStage =
                        selectedEnvId === currentEnvObj?.environment_id ||
                        isPreparingNextStage;

                      return (
                        <>
                          {/* HEADER */}
                          <div className="mb-4 flex justify-between items-center bg-gray-50 px-3 py-2 rounded">
                            <span className="text-sm font-bold text-gray-700">
                              Stage Information
                            </span>
                            {isPreparingNextStage && (
                              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded border border-yellow-200 font-semibold animate-pulse">
                                Preparing Next Stage...
                              </span>
                            )}
                            {!isPreparingNextStage &&
                              selectedEnvId ===
                                currentEnvObj?.environment_id && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded border border-green-200">
                                  Current Active
                                </span>
                              )}
                          </div>

                          {/* A. Dynamic Fields - COMPACT GRID */}
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                            {stageInputData.fields.map((field) => {
                              const noKeysDefaultValue =
                                onboardingDefaultKeys?.no_keys || "";
                              const resolvedFieldValue =
                                field.key === "nokey" &&
                                (!field.value || field.value.trim() === "")
                                  ? noKeysDefaultValue
                                  : field.value || "";
                              // LOCK Logic:
                              // If field has value -> Locked.
                              // If field is empty -> Editable.
                              const hasValue =
                                !!resolvedFieldValue &&
                                resolvedFieldValue.trim() !== "";
                              const isSubField = [
                                "subscriberid",
                                "subscriberurl",
                              ].includes(field.key);

                              let isDisabled = false;

                              if (!isEditableStage) {
                                isDisabled = true; // Totally locked if past stage
                              } else if (
                                field.key === "nokey" &&
                                !!noKeysDefaultValue
                              ) {
                                isDisabled = true;
                              } else {
                                // Current or Preparing Stage
                                if (isSubField) {
                                  isDisabled =
                                    hasValue && !isPreparingNextStage; // Lock if filled, unlock if empty
                                } else {
                                  isDisabled = false;
                                }
                              }

                              return (
                                <div
                                  key={field.key}
                                  className="flex flex-col gap-1"
                                >
                                  <label
                                    className="text-gray-500 text-[10px] uppercase font-bold tracking-wide truncate"
                                    title={field.key}
                                  >
                                    {field.key === "nokey"
                                      ? "NO Key"
                                      : field.key.toLowerCase() ===
                                        "subscriberid"
                                      ? "Subscriber Id"
                                      : field.key.toLowerCase() ===
                                        "subscriberurl"
                                      ? "Subscriber URL"
                                      : field.key.replace(/_/g, " ")}
                                  </label>
                                  <div className="relative">
                                    <input
                                      type="text"
                                      value={resolvedFieldValue}
                                      onChange={(e) =>
                                        handleStageFieldChange(
                                          field.key,
                                          e.target.value,
                                        )
                                      }
                                      disabled={isDisabled}
                                      className={`w-full border p-2 rounded text-xs transition-all ${
                                        isDisabled
                                          ? "bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200"
                                          : "bg-white text-gray-800 border-gray-300 focus:border-blue-500"
                                      }`}
                                      placeholder={
                                        !resolvedFieldValue
                                          ? "Enter Value"
                                          : ""
                                      }
                                    />
                                    {isDisabled && hasValue && (
                                      <FaLock
                                        size={10}
                                        className="absolute right-2 top-2.5 text-gray-400"
                                      />
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* B. Checklist - COMPACT SINGLE ROW GRID */}
                          {stageInputData.checklist &&
                            stageInputData.checklist.length > 0 && (
                              <div className="mt-4 border-t pt-3">
                                <h4 className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1">
                                  <ImCheckmark
                                    className="text-blue-500"
                                    size={10}
                                  />{" "}
                                  Checklist
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                  {stageInputData.checklist.map((item) => (
                                    <div
                                      key={item.key}
                                      onClick={() =>
                                        isEditableStage &&
                                        handleStageChecklistToggle(item.key)
                                      }
                                      className={`flex items-center gap-2 p-2 rounded border text-xs cursor-pointer select-none transition-all ${
                                        item.isDone
                                          ? "bg-blue-50 border-blue-300 text-blue-800"
                                          : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                                      } ${
                                        !isEditableStage
                                          ? "opacity-60 cursor-not-allowed"
                                          : ""
                                      }`}
                                    >
                                      <div
                                        className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                                          item.isDone
                                            ? "bg-blue-500 border-blue-500"
                                            : "bg-white border-gray-400"
                                        }`}
                                      >
                                        {item.isDone && (
                                          <TiTick
                                            className="text-white"
                                            size={12}
                                          />
                                        )}
                                      </div>
                                      <span className="truncate font-medium">
                                        {item.name}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                          {/* C. Action Buttons */}
                          {isEditableStage && (
                            <div className="flex justify-end gap-3 mt-6 pt-3 border-t">
                              {/* 1. Cancel Preparation (If preparing) */}
                              {isPreparingNextStage && (
                                <button
                                  onClick={() => {
                                    // Revert logic handled by tab click usually, but explicit cancel helps
                                    handleStageTabClick(
                                      currentEnvObj.environment_id,
                                    );
                                  }}
                                  className="px-4 py-1.5 rounded text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200"
                                >
                                  Cancel
                                </button>
                              )}

                              {/* 2. Save Progress (If Current Stage) */}
                              {!isPreparingNextStage && (
                                <button
                                  onClick={updateStageInfo}
                                  disabled={submitLoader}
                                  className="px-4 py-1.5 rounded text-xs font-medium text-gray-700 border border-gray-300 hover:bg-gray-50"
                                >
                                  {submitLoader ? "Saving..." : "Save Progress"}
                                </button>
                              )}

                              {/* 3. ACTION BUTTON: "Move Next" OR "Activate" */}
                              {isPreparingNextStage ? (
                                // ACTIVATE BUTTON (Calls API)
                                <button
                                  onClick={activateNextStage}
                                  disabled={submitLoader}
                                  className="bg-green-600 text-white px-5 py-1.5 rounded text-xs font-bold hover:bg-green-700 flex items-center gap-2"
                                >
                                  {submitLoader ? (
                                    <LoaderIcon
                                      size={14}
                                      className="animate-spin"
                                    />
                                  ) : (
                                    <FaCheckCircle size={12} />
                                  )}
                                  ACTIVATE STAGE
                                </button>
                              ) : (
                                // MOVE NEXT BUTTON (Changes View Only)
                                !isLastStage && (
                                  <button
                                    onClick={() => setShowConfirmModal(true)}
                                    className="bg-blue-600 text-white px-5 py-1.5 rounded text-xs font-bold hover:bg-blue-700 flex items-center gap-2"
                                  >
                                    Move to Next Stage{" "}
                                    <FaChevronRight size={10} />
                                  </button>
                                )
                              )}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
          </div>
          <div>
            {formData.sType === "subscription" && (
              <div className="mt-6">
                <table className="min-w-full mt-4 bg-white border border-gray-200 shadow-sm rounded-lg">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="py-2 px-4 text-left text-sm font-medium text-gray-600">
                        Subscription Name
                      </th>
                      <th className="py-2 px-4 text-left text-sm font-medium text-gray-600">
                        Period
                      </th>
                      <th className="py-2 px-4 text-left text-sm font-medium text-gray-600">
                        Purchase Date
                      </th>
                      <th className="py-2 px-4 text-left text-sm font-medium text-gray-600">
                        Price
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenantData?.subscriptionHistory?.map((history, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-4 text-gray-800">
                          {history.subscriptionName}
                        </td>

                        <td className="py-2 px-4 text-gray-800">
                          {new Intl.DateTimeFormat("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          }).format(new Date(history.dtStartDate))}{" "}
                          &nbsp; - &nbsp;
                          {new Intl.DateTimeFormat("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          }).format(new Date(history.dtEndDate))}
                        </td>
                        <td className="py-2 px-4 text-gray-600">
                          {new Intl.DateTimeFormat("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                            second: "numeric",
                            hour12: true,
                            timeZone: "Asia/Kolkata",
                          }).format(new Date(history.dtCreatedAt))}
                        </td>
                        <td className="py-2 px-4 text-gray-800">
                          {history.dAmount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div>
            {formData.sType === "domain" && (
              <>
                <div className="mt-6">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Domain</th>
                        <th>Status</th>
                        <th>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenantData?.whitelistedDomains?.length > 0 ? (
                        <>
                          {tenantData?.whitelistedDomains?.map(
                            (history, index) => (
                              <tr key={index}>
                                <td>{history.sDomain}</td>
                                <td>
                                  <div className="relative flex items-center">
                                    <input
                                      type="checkbox"
                                      id="status-switch"
                                      checked={history.bActive}
                                      onChange={() =>
                                        handleToggleActive(
                                          history,
                                          history.bActive,
                                        )
                                      }
                                      className="sr-only"
                                    />
                                    <div
                                      className={`w-10 h-6 flex items-center rounded-full p-1 cursor-pointer ${
                                        history.bActive
                                          ? "bg-green-500"
                                          : "bg-gray-300"
                                      }`}
                                      onClick={() =>
                                        handleToggleActive(
                                          history,
                                          history.bActive,
                                        )
                                      }
                                    >
                                      <div
                                        className={`w-4 h-4 bg-white rounded-full shadow-md transform ${
                                          history.bActive
                                            ? "translate-x-4"
                                            : "translate-x-0"
                                        }`}
                                      />
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  {new Intl.DateTimeFormat("en-US", {
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric",
                                  }).format(new Date(history.dtCreatedAt))}{" "}
                                </td>
                              </tr>
                            ),
                          )}
                        </>
                      ) : (
                        <tr>
                          <td colSpan={3} className="text-center">
                            No Domains Whitelisted
                          </td>
                        </tr>
                      )}

                      {whiteListedDomains.length > 0 &&
                        whiteListedDomains.map((domain, index) => {
                          return (
                            <tr>
                              <td colSpan={3}>
                                <div className="flex gap-4">
                                  <input
                                    type="text"
                                    name="sDomain"
                                    value={domain.sDomain}
                                    onChange={(e) =>
                                      handleInputChange(e, index)
                                    }
                                  />
                                  <span className="flex gap-4 items-center">
                                    {submitLoader ? (
                                      <button disabled={submitLoader}>
                                        <svg
                                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-500"
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                        >
                                          <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                          ></circle>
                                          <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                          ></path>
                                        </svg>
                                      </button>
                                    ) : (
                                      <button
                                        onClick={updateTenantDetails}
                                        disabled={!domain.sDomain}
                                      >
                                        <ImCheckmark
                                          size={20}
                                          className={`${
                                            !domain.sDomain
                                              ? "text-gray-400 cursor-not-allowed"
                                              : "text-green-500 cursor-pointer"
                                          }`}
                                        />
                                      </button>
                                    )}

                                    <button
                                      onClick={() => handleRemoveDomain(index)}
                                    >
                                      <FaXmark
                                        size={20}
                                        className="text-red-500 cursor-pointer"
                                      />
                                    </button>
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                {whiteListedDomains.length === 0 && (
                  <div className="flex">
                    <button
                      onClick={handleAddDomain}
                      className="addDomain hover:scale-[10@%]"
                    >
                      {" "}
                      + Add Domain
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            {formData.sType === "permission" && (
              <>
                {" "}
                <div className="flex items-center justify-center bg-gray-100 p-3 w-full">
                  <div className="p-3 w-full">
                    <h2 className="text-2xl font-semibold text-center text-gray-800 mb-6">
                      Manage Permissions
                    </h2>

                    <div className="grid grid-cols-4 gap-4">
                      {modulePermissions.map((permission) => (
                        <label
                          key={permission.iModulePermissionId}
                          className="flex items-center justify-between p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition cursor-pointer"
                        >
                          <span className="text-gray-700 font-medium text-sm">
                            {permission.sName.replace(/_/g, " ")}
                          </span>
                          <input
                            type="checkbox"
                            checked={selected.includes(
                              permission.iModulePermissionId,
                            )}
                            onChange={() =>
                              handleCheckboxChange(
                                permission.iModulePermissionId,
                              )
                            }
                            className="h-4 w-4 text-blue-600"
                          />
                        </label>
                      ))}
                    </div>
                    <div className="flex w-full items-end justify-end">
                      <button
                        onClick={updateTenantDetails}
                        className="mt-6 px-2 py-2 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition active:scale-95"
                      >
                        Save Permissions
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        !loader && (
          <p className="text-center text-gray-500 mt-6">No data available</p>
        )
      )}
      {showConfirmModal && (
        <Modal
          show={showConfirmModal}
          onHide={() => setShowConfirmModal(false)}
          centered
          size="sm"
        >
          <Modal.Header closeButton className="p-3">
            <Modal.Title className="text-md font-bold text-gray-800">
              Move to Next Stage?
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4 text-sm text-gray-600">
            Are you sure you want to proceed? This will lock the current stage
            and allow you to enter details for the next environment.
          </Modal.Body>
          <Modal.Footer className="p-2 bg-gray-50">
            <Button
              size="small"
              onClick={() => setShowConfirmModal(false)}
              className="text-gray-500"
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={onConfirmNextStage}
              className="bg-blue-600"
            >
              Yes, Proceed
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {isModalOpen && (
        <Modal
          show={isModalOpen}
          onHide={handleCloseModal}
          size="lg"
          centered
          className="subscriptionModal"
        >
          <Modal.Header closeButton>
            <h2 className="text-lg font-semibold">Choose a Billing Cycle</h2>
          </Modal.Header>
          <Modal.Body>
            <div className="px-6 py-3">
              <div className="flex flex-wrap justify-center gap-4 mb-3">
                {plans.map((cycle) => (
                  <button
                    key={cycle.iBillingCycleId}
                    className={`px-4 py-1 rounded-lg text-lg font-medium border transition duration-300 ease-in-out 
                                ${
                                  formData.subscriptionType ===
                                  cycle.iBillingCycleId
                                    ? "bg-blue-600 text-white border-blue-600 shadow-lg transform scale-105"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-blue-100 hover:border-blue-500"
                                }`}
                    onClick={() =>
                      handleSubscriptionTypeSelection(cycle.iBillingCycleId)
                    }
                  >
                    {cycle.billingCycleName}
                  </button>
                ))}
              </div>
              <div className="form-group">
                <label>Plan Type</label>
                <select
                  name="isStandard"
                  value={isStandard}
                  onChange={handleIsStandardChange}
                >
                  <option value="standard">Standard</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              {formData.subscriptionType && (
                <>
                  {!customLoader ? (
                    <div className="grid grid-cols-3 gap-6 h-[55vh] overflow-auto">
                      {plans
                        .find(
                          (cycle) =>
                            cycle.iBillingCycleId === formData.subscriptionType,
                        )
                        ?.plans.filter((plan) => plan.planName !== "Enterprise")
                        .map((plan) => {
                          const isSelected =
                            parseInt(formData?.selectedPlan) ===
                            plan.iPlanVersionId;
                          return (
                            <div
                              key={plan.iPlanVersionId}
                              className={`border rounded-lg p-4 shadow-md cursor-pointer transition-all flex flex-col min-h-[500px] ${
                                isSelected
                                  ? "border-blue-500 bg-blue-100 scale-105"
                                  : "border-gray-300"
                              }`}
                              onClick={() =>
                                handlePlanSelection(plan.iPlanVersionId)
                              }
                            >
                              <h4 className="text-xl font-semibold">
                                {plan.planName}
                              </h4>

                              <div className="flex justify-between items-center mt-2">
                                <span className="text-lg font-bold">
                                  {plan.currencySymbol}
                                  {plan.dPrice === -1 ? "0" : plan.dPrice}
                                </span>
                                {isSelected && (
                                  <FaCheckCircle className="text-green-600 text-2xl" />
                                )}
                              </div>

                              <div className="mt-4 space-y-2 flex-grow">
                                {plan.Features.map((feature, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center gap-2 text-gray-700"
                                  >
                                    <FaCheckCircle className="text-green-500 text-sm" />
                                    <span className="text-sm">
                                      {feature.sFieldType === "int" ? (
                                        <>
                                          {feature.sValue === "-1"
                                            ? "Unlimited"
                                            : `Upto ${feature.sValue}`}{" "}
                                          <strong>{feature.sFieldName}</strong>
                                        </>
                                      ) : (
                                        <>
                                          <strong>{feature.sFieldName}:</strong>{" "}
                                          {feature.sValue}
                                        </>
                                      )}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              {/* Fixed Button Position */}
                              <button
                                className={`mt-auto w-full py-2 rounded-lg text-white ${
                                  isSelected
                                    ? "bg-green-600"
                                    : "bg-black hover:bg-gray-800"
                                }`}
                              >
                                {isSelected ? "Current Plan" : "Choose Plan"}
                              </button>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <FullPageLoading />
                  )}
                </>
              )}
              {errors.selectedPlan && (
                <p className="text-red-500">{errors.selectedPlan}</p>
              )}
            </div>
          </Modal.Body>

          <Modal.Footer>
            <div className="flex justify-between gap-6">
              {submitLoader ? (
                <button
                  className="save-button"
                  style={{ width: "100px" }}
                  disabled={submitLoader}
                >
                  <LoaderIcon size={20} className="indicator" />
                </button>
              ) : (
                <button
                  type="submit"
                  className="save-button"
                  disabled={submitLoader}
                  onClick={updateTenantDetails} // Attach the function
                >
                  Update
                </button>
              )}
              <button
                type="submit"
                className="cancel-button"
                onClick={handleCloseModal}
              >
                Close
              </button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default TenantDetails;
